import { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { Pencil, Save, Eye, Loader2, Scissors } from "lucide-react";
import { setFileName, setLoadStore } from "../../state-manager/slices/preferences";
import { useSelector, useDispatch } from "react-redux";
import { saveTemplate } from "../../services/templateService";
import { updateElement, updateTemplate } from "../../services/overlayApi";
import { designsApi } from "../../services/designsApi";
import FeedSelectionModal from '../editor/FeedSelectionModal';



const Topbar = ({ store, templateId, creativeId }) => {
    const [isTitleOpen, setIsTitleOpen] = useState(false);
    const [tempFileName, setTempFileName] = useState("");
    const [currentPage, setCurrentPage] = useState(null);
    const [isFeedSelectionOpen, setIsFeedSelectionOpen] = useState(false);

    const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const fileName = useSelector((state) => state.preferences.fileName);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!store) return;

        const updateCurrentPage = () => {
            if (store.activePage) {
                setCurrentPage(store.activePage);
            }
        };

        updateCurrentPage();
        
        // Listen for selection changes
        const handleSelectionChange = () => {
             // Force re-render to update button state
             // We can use a local state to trigger this
             setCurrentPage({...store.activePage});
        };
        
        const handleChange = () => {
            updateCurrentPage();
        };

        const unsubscribe = store.on('change', handleChange);
        const unsubscribeSelection = store.on('selectionChange', handleSelectionChange);

        return () => {
            if (unsubscribe) unsubscribe();
            if (unsubscribeSelection) unsubscribeSelection();
        };
    }, [store]);

    const handleTitleSave = async () => {
        dispatch(setFileName(tempFileName));
        setIsTitleOpen(false);

        if (creativeId) {
            try {
                await designsApi.update(creativeId, {
                    name: tempFileName
                });
            } catch (error) {
                console.error("Failed to update design name:", error);
                alert("Failed to update name: " + error.message);
            }
        }
    };






    const handleSaveTemplate = async () => {
        // If we have a creativeId, update via designs API
        if (creativeId) {
            setIsSaving(true);
            try {
                const json = store.toJSON();
                await designsApi.update(creativeId, {
                    canvas_data: json
                });
                alert("Design updated successfully!");
            } catch (error) {
                console.error("Error updating design:", error);
                alert("Failed to update design: " + error.message);
            } finally {
                setIsSaving(false);
            }
            return;
        }

        // If we have a templateId, update via API
        if (templateId) {
            setIsSaving(true);
            try {
                const json = store.toJSON();
                await updateTemplate(templateId, {
                    canvas_data: json
                });
            } catch (error) {
                console.error("Error updating template:", error);
                alert("Failed to update template: " + error.message);
            } finally {
                setIsSaving(false);
            }
            return;
        }

        // Legacy behavior: save to localStorage
        if (!templateName.trim()) {
            alert("Please enter a template name");
            return;
        }

        try {
            // Get current canvas JSON
            const json = store.toJSON();
            
            // Extract variable map from elements with custom.isVariable
            const variableMap = {};
            json.pages.forEach(page => {
                page.children.forEach(element => {
                    if (element.custom?.isVariable) {
                        variableMap[element.id] = {
                            type: element.custom.variableType,
                            variableName: element.custom.variableName,
                            ...(element.custom.variableType === 'image' && {
                                imageIndex: element.custom.imageIndex || 0
                            })
                        };
                    }
                });
            });

            // Save template
            saveTemplate(templateName, json, variableMap);
            
            alert("Template saved successfully!");
            setIsSaveTemplateOpen(false);
            setTemplateName("");
        } catch (error) {
            console.error("Error saving template:", error);
            alert("Failed to save template: " + error.message);
        }
    };
    
    const [isRemovingBackground, setIsRemovingBackground] = useState(false);

    const handleRemoveBackground = async () => {
        const apiKey = process.env.REACT_APP_POLOTNO_API_KEY;
        if (!apiKey) {
            alert("Polotno API Key is missing. Please check your .env file.");
            return;
        }

        const selected = store.selectedElements;
        if (selected.length !== 1 || selected[0].type !== 'image') return;

        const element = selected[0];
        setIsRemovingBackground(true);

        try {
            const req = await fetch(`https://api.polotno.com/api/remove-image-background?KEY=${apiKey}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ url: element.src }),
            });
            
            if (!req.ok) {
                const errorData = await req.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to remove background');
            }

            const res = await req.json();
            // Update the element with the new image
            element.set({ src: res.url });
        } catch (error) {
            console.error("Background removal error:", error);
            alert("Failed to remove background: " + error.message);
        } finally {
            setIsRemovingBackground(false);
        }
    };


    const handleSelectFeed = () => {
        setIsFeedSelectionOpen(true);
    };





    return (
        <div className="flex items-center justify-between sticky w-full z-10 h-[64px] px-4"
            style={{ background: 'linear-gradient(to left, #CCE5FF -14.26%, #7885D8 32.58%)' }}>

            {/* Left side - Template title */}
            <div className="flex items-center gap-2 text-white">
                <span className="font-medium">{fileName || "Untitled Design"}</span>
                <Pencil className="w-4 h-4 cursor-pointer hover:scale-110 transition-transform" onClick={() => {
                    setTempFileName(fileName);
                    setIsTitleOpen(true);
                }} />
            </div>

            {/* Right side - Controls */}
            <div className="flex items-center gap-3 text-white">
                {/* Remove Background Button */}
                {store.selectedElements.length === 1 && store.selectedElements[0].type === 'image' && (
                    <button
                        onClick={handleRemoveBackground}
                        disabled={isRemovingBackground}
                        className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-white flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isRemovingBackground ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm">Processing...</span>
                            </>
                        ) : (
                            <>
                                <Scissors className="w-4 h-4" />
                                <span className="text-sm">Remove BG</span>
                            </>
                        )}
                    </button>
                )}

                {/* Save Template */}
                <button
                    onClick={() => (templateId || creativeId) ? handleSaveTemplate() : setIsSaveTemplateOpen(true)}
                    disabled={isSaving}
                    className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-white flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSaving ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Saving...</span>
                        </>
                    ) : (
                        <>
                            <Save className="w-4 h-4" />
                            <span className="text-sm">Save {(templateId || creativeId) ? 'Design' : 'Template'}</span>
                        </>
                    )}
                </button>

                {/* Select Feed */}
                <button
                    onClick={handleSelectFeed}
                    className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-white flex items-center gap-2 transition-colors"
                >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">Select Feed</span>
                </button>
            </div>

            {/* Modal for editing filename */}
            <Dialog open={isTitleOpen} onClose={() => setIsTitleOpen(false)} className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <Dialog.Panel className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                    <Dialog.Title className="text-lg font-semibold mb-4">Edit Title</Dialog.Title>
                    <input
                        type="text"
                        value={tempFileName}
                        onChange={(e) => setTempFileName(e.target.value)}
                        className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter title"
                    />
                    <div className="flex justify-end gap-2 mt-4">
                        <button onClick={() => setIsTitleOpen(false)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors">Cancel</button>
                        <button onClick={handleTitleSave} className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors">Save</button>
                    </div>
                </Dialog.Panel>
            </Dialog>

            {/* Modal for saving template */}
            <Dialog open={isSaveTemplateOpen} onClose={() => setIsSaveTemplateOpen(false)} className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <Dialog.Panel className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                    <Dialog.Title className="text-lg font-semibold mb-4">Save as Template</Dialog.Title>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                        <input
                            type="text"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter template name"
                        />
                    </div>
                    <div className="text-sm text-gray-600 mb-4">
                        This will save your current design as a reusable template. Elements marked as variables will be replaced when applying to products.
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setIsSaveTemplateOpen(false)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors">Cancel</button>
                        <button onClick={handleSaveTemplate} className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors">Save Template</button>
                    </div>
                </Dialog.Panel>
            </Dialog>

            {/* Feed Selection Modal */}
            {isFeedSelectionOpen && (
                <FeedSelectionModal 
                    store={store} 
                    isOpen={isFeedSelectionOpen}
                    onClose={() => setIsFeedSelectionOpen(false)} 
                />
            )}
        </div>
    );
};

export default Topbar;
